import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useCallback, useRef, useState, useEffect } from 'react';

import {
  SelectionBounds,
  ViewportInfo,
  constrainSelectionTime,
} from '../utils/selectionCalculations';

interface DragStartInfo {
  startTime: number;
  duration: number;
  startX: number;
  initialScrollLeft: number;
}

interface UsePragmaticDragProps {
  onSelectionMove?: (startTime: number) => void;
  onVideoTimeUpdate?: (time: number) => void;
  onClearKeyboardFocus?: () => void;
  instanceId: symbol;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  isPlaying?: boolean;
  onPlayPause?: () => void;
}

export function usePragmaticDrag({
  onSelectionMove,
  onVideoTimeUpdate,
  onClearKeyboardFocus,
  instanceId,
  scrollContainerRef,
  isPlaying = false,
  onPlayPause,
}: UsePragmaticDragProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<DragStartInfo | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const wasPlayingBeforeDragRef = useRef<boolean>(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const cleanup = draggable({
      element,
      getInitialData: () => ({
        type: 'selection-indicator',
        startTime: parseFloat(element.dataset.startTime || '0'),
        duration: parseFloat(element.dataset.duration || '0'),
        instanceId,
      }),
      onDragStart: ({ location }) => {
        setIsDragging(true);
        onClearKeyboardFocus?.();

        // Store video playing state and pause if playing
        wasPlayingBeforeDragRef.current = isPlaying;
        if (isPlaying && onPlayPause) {
          onPlayPause(); // Pause video during drag
        }

        const startTime = parseFloat(element.dataset.startTime || '0');
        dragStartRef.current = {
          startTime,
          duration: parseFloat(element.dataset.duration || '0'),
          startX: location.initial.input.clientX,
          initialScrollLeft: scrollContainerRef?.current?.scrollLeft || 0,
        };
      },
      onDrag: () => {
        if (!dragStartRef.current) return;
      },
      onDrop: ({ location }) => {
        setIsDragging(false);

        // Check if dropped on a valid target
        const dropTargets = location.current.dropTargets;
        const isValidDrop = dropTargets.some(
          (target) => target.data.type === 'film-strip-drop-target'
        );

        if (dragStartRef.current) {
          if (isValidDrop) {
            // Valid drop: Calculate new position based on drag movement
            const currentX = location.current.input.clientX;
            const currentScrollLeft =
              scrollContainerRef?.current?.scrollLeft || 0;

            // Calculate both mouse movement and scroll movement
            const mouseMovement = currentX - dragStartRef.current.startX;
            const scrollMovement =
              currentScrollLeft - dragStartRef.current.initialScrollLeft;

            // Total movement accounts for both mouse drag and auto-scroll
            const totalMovement = mouseMovement + scrollMovement;

            const videoDuration = parseFloat(
              element.dataset.videoDuration || '0'
            );
            const stripWidth = parseFloat(element.dataset.stripWidth || '0');

            const timeDelta = (totalMovement * videoDuration) / stripWidth;
            const rawNewStartTime = dragStartRef.current.startTime + timeDelta;
            const newStartTime = constrainSelectionTime(
              rawNewStartTime,
              dragStartRef.current.duration,
              videoDuration
            );

            onSelectionMove?.(newStartTime);
            // Update video time to final position
            onVideoTimeUpdate?.(newStartTime);
          } else {
            // Invalid drop: Reset to original position and scroll back
            onSelectionMove?.(dragStartRef.current.startTime);
            // Update video time back to original position
            onVideoTimeUpdate?.(dragStartRef.current.startTime);

            // Scroll back to original position if auto-scroll occurred
            if (
              scrollContainerRef?.current &&
              dragStartRef.current.initialScrollLeft !==
                scrollContainerRef.current.scrollLeft
            ) {
              scrollContainerRef.current.scrollTo({
                left: dragStartRef.current.initialScrollLeft,
                behavior: 'smooth',
              });
            }
          }
        }

        // Resume video playback if it was playing before drag started
        if (wasPlayingBeforeDragRef.current && onPlayPause) {
          onPlayPause(); // Resume video playback
        }

        dragStartRef.current = null;
      },
    });

    return cleanup;
  }, [
    onSelectionMove,
    onVideoTimeUpdate,
    onClearKeyboardFocus,
    instanceId,
    scrollContainerRef,
    isPlaying,
    onPlayPause,
  ]);

  const updateElementData = useCallback(
    (selection: SelectionBounds, viewport: ViewportInfo) => {
      if (elementRef.current) {
        elementRef.current.dataset.startTime = selection.startTime.toString();
        elementRef.current.dataset.duration = selection.duration.toString();
        elementRef.current.dataset.videoDuration =
          viewport.videoDuration.toString();
        elementRef.current.dataset.stripWidth = viewport.stripWidth.toString();
      }
    },
    []
  );

  return {
    isDragging,
    elementRef,
    updateElementData,
  };
}
